/* Tema e densidade.
 *
 * Os dois são atributos do `<html>` (`data-tema`, `data-densidade`), porque é
 * assim que os tokens do design system chaveiam. Guardar no React e passar por
 * prop obrigaria cada componente a saber do tema — e os componentes do design
 * system, de propósito, não sabem: eles só leem variáveis CSS.
 *
 * A preferência fica em `localStorage`, não no servidor: é escolha do aparelho
 * (o monitor da sala é diferente do notebook em casa), não da conta.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const Contexto = createContext(null);

const CHAVE_TEMA = 'painel:tema';
const CHAVE_DENSIDADE = 'painel:densidade';

const TEMAS = ['claro', 'escuro'];
const DENSIDADES = ['compacta', 'padrao', 'confortavel'];

function guardado(chave, validos, padrao) {
  try {
    const valor = localStorage.getItem(chave);
    return validos.includes(valor) ? valor : padrao;
  } catch {
    // Navegação privada pode negar o acesso. Preferência não é essencial.
    return padrao;
  }
}

function temaDoSistema() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'claro';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'escuro' : 'claro';
}

export function ProvedorPreferencias({ children }) {
  const [tema, definirTema] = useState(() =>
    guardado(CHAVE_TEMA, TEMAS, temaDoSistema()),
  );

  // Compacta é o padrão porque quem passa o turno inteiro no painel é o
  // atendente — e a lista de conversas cabe mais na tela.
  const [densidade, definirDensidade] = useState(() =>
    guardado(CHAVE_DENSIDADE, DENSIDADES, 'compacta'),
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-tema', tema);
    // Diz ao navegador qual esquema usar em barra de rolagem nativa e campos.
    document.documentElement.style.colorScheme = tema === 'escuro' ? 'dark' : 'light';
    try {
      localStorage.setItem(CHAVE_TEMA, tema);
    } catch {
      /* preferência não é essencial */
    }
  }, [tema]);

  useEffect(() => {
    document.documentElement.setAttribute('data-densidade', densidade);
    try {
      localStorage.setItem(CHAVE_DENSIDADE, densidade);
    } catch {
      /* preferência não é essencial */
    }
  }, [densidade]);

  const alternarTema = useCallback(
    () => definirTema((atual) => (atual === 'claro' ? 'escuro' : 'claro')),
    [],
  );

  return (
    <Contexto.Provider value={{ tema, densidade, definirTema, definirDensidade, alternarTema }}>
      {children}
    </Contexto.Provider>
  );
}

export function usePreferencias() {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('usePreferencias fora do ProvedorPreferencias');
  return contexto;
}
